import { AbstractControl, FormArray, FormGroup } from '@angular/forms';

/**
 * Recursively find the path to the first invalid control in a form (e.g. "height", "horse_details.stable_name").
 */
function getFirstInvalidPath(control: AbstractControl, prefix = ''): string | null {
    if (!control || !control.invalid) return null;
    if (control instanceof FormGroup) {
        for (const key of Object.keys(control.controls)) {
            const child = control.get(key);
            if (!child) continue;
            const path = prefix ? prefix + '.' + key : key;
            if (child.invalid) {
                if (child instanceof FormGroup || child instanceof FormArray) {
                    const nested = getFirstInvalidPath(child, path);
                    if (nested) return nested;
                }
                return path;
            }
        }
        return null;
    }
    if (control instanceof FormArray) {
        for (let i = 0; i < control.length; i++) {
            const child = control.at(i);
            const path = prefix ? prefix + '.' + i : String(i);
            if (child.invalid) {
                if (child instanceof FormGroup || child instanceof FormArray) {
                    const nested = getFirstInvalidPath(child, path);
                    if (nested) return nested;
                }
                return path;
            }
        }
        return null;
    }
    return prefix || null;
}

/**
 * Scroll the first invalid form control into view. Call after markAllAsTouched() when form is invalid.
 * @param form FormGroup (reactive form)
 * @param hostElement Optional element to search within (e.g. component host). If not set, uses document.
 */
export function scrollToFirstInvalidControl(form: FormGroup, hostElement?: HTMLElement): void {
    const path = getFirstInvalidPath(form);
    if (!path) return;
    const leafName = path.split('.').pop() || path;
    const root = hostElement || document;
    const rootEl = root as Document | HTMLElement;
    let el = rootEl.querySelector ? rootEl.querySelector('[formcontrolname="' + leafName + '"]') : null;
    if (!el && rootEl.querySelector) {
        el = rootEl.querySelector('.ng-invalid');
    }
    if (el && typeof (el as HTMLElement).scrollIntoView === 'function') {
        (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
}

/**
 * Capitalize every new word in a string
 * @param name 
 * @returns 
 */
export function capitalizeName(name: string) {
    if (!name) {
        return '';
    }

    const parts = name.split(' ');

    const formattedParts = parts.map(part => {
        // Check if the part is a number or empty space
        const isNumber = !isNaN(Number(part)) && part.trim() !== '';

        if (isNumber) {
            return part;
        }

        // Check if it's just empty space (handles double spaces)
        if (part.trim() === '') {
            return part;
        }

        // Split by hyphen first to handle composite words
        // If there is no hyphen, this simply returns an array with one item (the word itself)
        return part.split('-').map(segment => {
            // Handle edge case of multiple hyphens or trailing hyphens
            if (!segment) return '';
            
            // Capitalize first letter of the segment, lowercase the rest
            return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
        }).join('-');
    });

    return formattedParts.join(' ');
}

/**
 * Capitalize only the first letter of each word; leave the rest unchanged.
 * Use for horse names so roman numerals (e.g. XVI, VIII) stay in capitals.
 * "henry" → "Henry", "XVI" → "XVI", "henry viii" → "Henry Viii"
 */
export function capitalizeHorseName(name: string): string {
    if (!name) return '';
    return name.split(/\s+/).map(part => {
        if (!part) return part;
        return part.split('-').map(segment => {
            if (!segment) return '';
            return segment.charAt(0).toUpperCase() + segment.slice(1);
        }).join('-');
    }).join(' ');
}

export function getEquineTransactionType(quote: any): number {
    return Number(quote?.transaction_type ?? quote?.type ?? 0);
}

/** Quote id required after Quote Details; null/empty means a new row would be created. */
export function requireEquineQuoteId(model: any): string | null {
    const id = model?.id;
    if (id == null || id === '') {
        return null;
    }
    return String(id);
}

const QUOTE_DETAILS_HORSE_REQUIRED: Array<[string, string]> = [
    ['purchase_price', 'Purchase Price'],
    ['horse_birth_year', 'Year of Birth'],
];
const ADDITIONAL_HORSE_REQUIRED: Array<[string, string]> = [
    ['height', 'Height'],
    ['breed_id', 'Breed'],
    ['color_id', 'Colour'],
    ['gender_id', 'Sex'],
];

function horseDetailMissing(hd: any, key: string): boolean {
    const value = hd?.[key] ?? (key === 'horse_birth_year' ? hd?.birth_year : undefined);
    if (key === 'purchase_price') {
        return value == null || value === '';
    }
    if (key === 'is_horse_vetted' || key === 'is_insured_by_you') {
        return value != 1 && value != 2;
    }
    return value == null || value === '' || Number(value) === 0;
}

/**
 * Required horse details missing from a saved quote. Page 1 fields are listed first.
 */
export function getMissingEquineHorseDetails(model: any): string[] {
    const hd = model?.horse_details || model?.horse_Details || {};
    return [...QUOTE_DETAILS_HORSE_REQUIRED, ...ADDITIONAL_HORSE_REQUIRED]
        .filter(([key]) => horseDetailMissing(hd, key))
        .map(([, label]) => label);
}

export function missingEquineHorseDetailsTab(model: any): number {
    const hd = model?.horse_details || model?.horse_Details || {};
    const quoteDetailsMissing = QUOTE_DETAILS_HORSE_REQUIRED.some(([key]) => horseDetailMissing(hd, key));
    return quoteDetailsMissing ? 0 : 1;
}

export function resolveEquinePreviousPolicyId(quote: any): string | null {
    if (quote?.previous_policy_id) {
        return quote.previous_policy_id;
    }
    const txType = getEquineTransactionType(quote);
    if (txType !== 3 && txType !== 4) {
        return null;
    }
    const quoteId = quote?.id;
    const storedPreviousId = quote?.old_policy_id || quote?.horse_details?.policy_id;
    if (storedPreviousId && storedPreviousId !== quoteId) {
        return storedPreviousId;
    }
    const versions = quote?.quote_versions;
    if (!Array.isArray(versions) || quote?.sequence_number == null) {
        return null;
    }
    const prev = versions
        .filter((v: any) => Number(v.sequence_number) < Number(quote.sequence_number))
        .sort((a: any, b: any) => Number(b.sequence_number) - Number(a.sequence_number))[0];
    return prev?.id ?? null;
}

export function isEquineTransactionJourneyOnPolicy(quote: any): boolean {
    const txType = getEquineTransactionType(quote);
    if (txType !== 3 && txType !== 4) {
        return false;
    }
    const previousPolicyId = quote?.previous_policy_id;
    const quoteId = quote?.id;
    return !!previousPolicyId && !!quoteId && previousPolicyId === quoteId;
}

export function getEquineDisplayPremiumData(quote: any, adjusted?: any): any {
    if (adjusted) {
        return adjusted;
    }
    const txType = getEquineTransactionType(quote);
    if (isEquineTransactionJourneyOnPolicy(quote)) {
        return quote?.premiums?.data ?? null;
    }
    if (txType === 3 || txType === 4) {
        return quote?.financial_details ?? quote?.premiums?.data ?? null;
    }
    return quote?.premiums?.data ?? quote?.financial_details ?? null;
}